import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BedDouble, IndianRupee, TrendingUp, Users, Target, Pencil } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { BRAND, errMsg, inr } from "./constants";
import { LedgerPanel } from "./LedgerPanel";
import { BookingsPanel } from "./BookingsPanel";

function StatTile({ icon: Icon, label, value, hint, tid, accent = BRAND.green }) {
  return (
    <div data-testid={tid} className="rounded-2xl border-2 bg-white p-4 hs-rise" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
      </div>
      <div className="mt-1 font-display text-2xl md:text-3xl font-bold" style={{ color: accent }} data-testid={`${tid}-value`}>{value}</div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: BRAND.muted }}>{hint}</div>}
    </div>
  );
}

function TargetBar({ target, onEdit }) {
  return (
    <div data-testid="target-progress" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold" style={{ color: BRAND.green }}><Target className="w-4 h-4" /> Monthly target</div>
        <button data-testid="target-edit-btn" onClick={onEdit} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: BRAND.orange }}><Pencil className="w-3 h-3" /> Edit</button>
      </div>
      <div className="mt-3 h-4 rounded-full overflow-hidden" style={{ background: "#EEF2F0" }}>
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${target.pct}%`, background: `linear-gradient(90deg, ${BRAND.green}, ${BRAND.orange})` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs" style={{ color: BRAND.muted }}>
        <span data-testid="target-achieved">{inr(target.achieved_inr)} achieved</span>
        <span data-testid="target-pct" className="font-bold" style={{ color: BRAND.orange }}>{target.pct}% of {inr(target.monthly_target_inr)}</span>
      </div>
    </div>
  );
}

function PipelineCard({ p }) {
  const rows = [["Open enquiries", p.enquiries, p.enquiry_value, "pipeline-enquiries"], ["Confirmed (not yet checked-in)", p.confirmed_upcoming, p.confirmed_value, "pipeline-confirmed"]];
  return (
    <div data-testid="pipeline-card" className="rounded-2xl p-4 text-white" style={{ background: BRAND.green }}>
      <div className="flex items-center gap-2 text-sm font-bold"><TrendingUp className="w-4 h-4" /> Pipeline forecast</div>
      <div className="mt-2 font-display text-3xl font-bold" data-testid="pipeline-forecast">{inr(p.forecast_inr)}</div>
      <div className="text-[11px] text-white/75">This month's revenue + confirmed + 40% of enquiries</div>
      <div className="mt-3 space-y-1.5 text-sm">
        {rows.map(([l, n, v, t]) => (
          <div key={t} data-testid={t} className="flex justify-between border-t border-white/15 pt-1.5"><span>{l} ({n})</span><b>{inr(v)}</b></div>
        ))}
      </div>
    </div>
  );
}

function RevenueChart({ series }) {
  const data = series.map((s) => ({ ...s, label: new Date(`${s.month}-01`).toLocaleString("en-IN", { month: "short" }) }));
  return (
    <div data-testid="revenue-chart" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold mb-3" style={{ color: BRAND.green }}>Revenue vs expenses · last 6 months</div>
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F0" />
            <XAxis dataKey="label" fontSize={11} />
            <YAxis fontSize={11} width={48} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip formatter={(v) => inr(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="revenue" name="Revenue" fill={BRAND.green} radius={[6, 6, 0, 0]} />
            <Bar dataKey="expenses" name="Expenses" fill={BRAND.orange} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function CommandCentre({ pid, authFetch, API }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/dashboard` })
    .then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e, "Could not load dashboard"))), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const editTarget = async () => {
    const v = window.prompt("Monthly revenue target (₹)", d?.target.monthly_target_inr);
    if (v === null || isNaN(+v)) return;
    try { await authFetch({ method: "patch", url: `${API}/homestay/properties/${pid}`, data: { monthly_target_inr: Math.max(0, Math.round(+v)) } }); toast.success("Target updated"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not update target")); }
  };

  if (!d) return <div className="text-sm" style={{ color: BRAND.muted }} data-testid="cc-loading">Loading Command Centre…</div>;
  const o = d.overview;
  return (
    <div data-testid="command-centre" className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile tid="stat-occupancy" icon={BedDouble} label="Occupancy" value={`${o.occupancy_pct}%`} hint={`${o.occupied}/${o.total_beds} beds · ${o.vacant} vacant`} />
        <StatTile tid="stat-revenue" icon={IndianRupee} label="Revenue (month)" value={inr(o.month_revenue)} hint="Ledger + checked-in bookings" />
        <StatTile tid="stat-expenses" icon={IndianRupee} label="Expenses (month)" value={inr(o.month_expenses)} accent={BRAND.orange} hint={`Profit ${inr(o.month_profit)}`} />
        <StatTile tid="stat-guests" icon={Users} label="Active guests" value={o.active_guests} hint={`${o.bed_counts.reserved} reserved · ${o.bed_counts.checkout_due} checkout due`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><RevenueChart series={d.series} /></div>
        <div className="space-y-4"><TargetBar target={d.target} onEdit={editTarget} /><PipelineCard p={d.pipeline} /></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <BookingsPanel pid={pid} authFetch={authFetch} API={API} onChange={load} />
        <LedgerPanel pid={pid} authFetch={authFetch} API={API} onChange={load} />
      </div>
    </div>
  );
}
